import React, { useState } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Spin, Alert, Card, Typography, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StopOutlined, InfoCircleOutlined, TeamOutlined, CheckCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetBrandsQuery,
    useGetBrandCohortsQuery,
    useCreateBrandCohortMutation,
    useUpdateBrandCohortMutation,
    useDeactivateBrandCohortMutation,
    useDeleteBrandCohortMutation
} from '../../services/nestjs-base-api';
import { ISurveyCohort } from '../../services/nestjs-base-api/types';
import CohortFormModal from './CohortFormModal';

const { Paragraph, Text } = Typography;

interface CohortManagementProps {}

const CohortManagement: React.FC<CohortManagementProps> = () => {
    const { user } = useAuth();
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCohort, setSelectedCohort] = useState<ISurveyCohort | null>(null);

    // Fetch brands to get the brand ID (assuming single brand for now)
    const { data: brands, isLoading: brandsLoading } = useGetBrandsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    const brandId = brands && brands.length > 0 ? brands[0].id : null;

    // Fetch brand cohorts
    const { data: cohorts, isLoading: cohortsLoading, error: cohortsError } = useGetBrandCohortsQuery({
        jwtToken: user?.jwtToken!,
        brandId: brandId!
    }, {
        skip: !user?.jwtToken || !brandId
    });

    // Mutations
    const [createCohort] = useCreateBrandCohortMutation();
    const [updateCohort] = useUpdateBrandCohortMutation();
    const [deactivateCohort] = useDeactivateBrandCohortMutation();
    const [deleteCohort] = useDeleteBrandCohortMutation();

    // Filter system cohorts for parent selection
    const systemCohorts = (cohorts || []).filter((c: ISurveyCohort) => c.cohortType === 'system');

    const columns = [
        {
            title: 'Cohort Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (text: string, record: any) => (
                <Space>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                    {record.cohortType === 'system' && (
                        <Tag color="blue">System</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Parent Cohort',
            dataIndex: 'parentCohort',
            key: 'parentCohort',
            render: (parent: any) => parent?.displayName || '-',
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'default'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => {
                // System cohorts cannot be edited or deleted
                if (record.cohortType === 'system') {
                    return <span style={{ color: '#999' }}>-</span>;
                }

                return (
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        >
                            Edit
                        </Button>
                        {record.isActive && (
                            <Popconfirm
                                title="Deactivate Cohort"
                                description="This will hide the cohort from new survey creation. Existing surveys will not be affected."
                                onConfirm={() => handleDeactivate(record.id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<StopOutlined />}
                                    danger
                                >
                                    Deactivate
                                </Button>
                            </Popconfirm>
                        )}
                        <Popconfirm
                            title="Delete Cohort"
                            description="Are you sure you want to delete this cohort? This action cannot be undone."
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="link"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const handleCreate = () => {
        setSelectedCohort(null);
        setModalVisible(true);
    };

    const handleEdit = (cohort: ISurveyCohort) => {
        setSelectedCohort(cohort);
        setModalVisible(true);
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setSelectedCohort(null);
    };

    const handleFormSubmit = async (values: any) => {
        if (!brandId) return;

        try {
            if (selectedCohort) {
                // Update existing cohort
                await updateCohort({
                    jwtToken: user?.jwtToken!,
                    brandId,
                    cohortId: selectedCohort.id,
                    data: values
                }).unwrap();
                message.success('Cohort updated successfully');
            } else {
                // Create new cohort
                await createCohort({
                    jwtToken: user?.jwtToken!,
                    brandId,
                    data: values
                }).unwrap();
                message.success('Cohort created successfully');
            }
            setModalVisible(false);
            setSelectedCohort(null);
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to save cohort');
            throw error; // Re-throw to prevent modal from closing
        }
    };

    const handleDeactivate = async (cohortId: number) => {
        if (!brandId) return;

        try {
            await deactivateCohort({
                jwtToken: user?.jwtToken!,
                brandId,
                cohortId
            }).unwrap();
            message.success('Cohort deactivated successfully');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to deactivate cohort');
        }
    };

    const handleDelete = async (cohortId: number) => {
        if (!brandId) return;

        try {
            await deleteCohort({
                jwtToken: user?.jwtToken!,
                brandId,
                cohortId
            }).unwrap();
            message.success('Cohort deleted successfully');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to delete cohort');
        }
    };

    if (brandsLoading || cohortsLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!brandId) {
        return (
            <Alert
                message="No Brand Found"
                description="Please create a brand first before managing cohorts."
                type="warning"
                showIcon
            />
        );
    }

    if (cohortsError) {
        return (
            <Alert
                message="Error Loading Cohorts"
                description="There was an error loading cohorts. Please try again later."
                type="error"
                showIcon
            />
        );
    }

    return (
        <div>
            {/* Explanation Card */}
            <Card
                style={{
                    marginBottom: 24,
                    background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                    border: 'none'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <InfoCircleOutlined style={{ fontSize: 24, color: 'white', marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                        <Text strong style={{ color: 'white', fontSize: 16, display: 'block', marginBottom: 12 }}>
                            Why Create Custom Cohorts?
                        </Text>
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: 8,
                            padding: 16
                        }}>
                            <Paragraph style={{ marginBottom: 12 }}>
                                Custom cohorts allow you to segment your customers for more targeted brand equity tracking.
                                While we provide system cohorts (like "Recent Customers" and "Past Customers"), you can create
                                your own cohorts to match your specific business needs.
                            </Paragraph>
                            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                                <TeamOutlined style={{ marginRight: 8, color: '#F59E0B' }} />
                                Common Use Cases:
                            </Text>
                            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                <li style={{ marginBottom: 6 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                    <Text strong>VIP Customers:</Text> Track brand equity among your highest-value customers
                                </li>
                                <li style={{ marginBottom: 6 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                    <Text strong>Product-Specific Segments:</Text> Survey customers who purchased specific product lines
                                </li>
                                <li style={{ marginBottom: 0 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                    <Text strong>Lifecycle Stages:</Text> Different cohorts for trial users, active subscribers, or churned customers
                                </li>
                            </ul>

                            <Divider style={{ margin: '16px 0' }} />

                            <div style={{
                                background: '#fff7e6',
                                border: '1px solid #ffa940',
                                borderRadius: 6,
                                padding: 12
                            }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#d46b08' }}>
                                    <MailOutlined style={{ marginRight: 8 }} />
                                    Important: Survey Distribution
                                </Text>
                                <Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
                                    After creating cohorts and surveys, you'll need to send the survey links to your customers
                                    using your own email marketing platform (MailChimp, HubSpot, Klaviyo, etc.). Make sure to
                                    segment your email list to match the cohort definition you've created here, so the right
                                    customers receive the right surveys.
                                </Paragraph>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                        Manage customer cohorts for your surveys. System cohorts cannot be edited or deleted.
                    </p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Create Cohort
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={cohorts || []}
                rowKey="id"
                pagination={false}
            />

            <CohortFormModal
                visible={modalVisible}
                cohort={selectedCohort}
                systemCohorts={systemCohorts}
                onCancel={handleModalCancel}
                onSubmit={handleFormSubmit}
            />
        </div>
    );
};

export default CohortManagement;
